import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import moment from 'moment';

export interface ICheckDBHistory {
  id: number;
  nome: string;
  status: string;
  dataHora: string;
  resultado: string;
}

interface IProps {
  timeline: ICheckDBHistory[];
}

export default function CheckDBTimeline({ timeline }: IProps) {
  const theme = useTheme();

  if (!timeline || timeline.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <FactCheckIcon color="primary" /> Histórico de Integridade de Banco de Dados
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pl: 1 }}>
          Nenhum diagnóstico de integridade executado nos últimos dias.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <FactCheckIcon color="primary" /> Histórico de Integridade de Banco de Dados
      </Typography>
      
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 2, 
        position: 'relative', 
        pl: 2.5, 
        borderLeft: `2px solid ${theme.palette.divider}`,
        ml: 1
      }}>
        {timeline.map((item, index) => {
          const isSucesso = ["sucesso", "executada", "concluido", "concluído"].includes(item.status?.toLowerCase());
          const corStatus = isSucesso ? theme.palette.success.main : theme.palette.error.main;
          const Icone = isSucesso ? CheckCircleIcon : CancelIcon;

          return (
            <Box key={item.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, position: 'relative', mb: index === timeline.length - 1 ? 0 : 1 }}>
              {/* Timeline Connector Dot */}
              <Box sx={{ 
                position: 'absolute', 
                left: -29, 
                top: 4, 
                backgroundColor: theme.palette.background.paper, 
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                padding: '2px'
              }}>
                <Icone sx={{ color: corStatus, fontSize: 18 }} />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                    {item.nome}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {moment(item.dataHora).format('DD/MM/YYYY [às] HH:mm')}
                  </Typography>
                </Box>
                {item.resultado && !isSucesso && (
                  <Typography variant="body2" sx={{ 
                    mt: 1, 
                    p: 1.5, 
                    borderRadius: 2, 
                    fontSize: '11px',
                    fontFamily: 'Consolas, Monaco, monospace',
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.15)' : '#fdecea',
                    color: theme.palette.error.main,
                    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.3)' : '#fcd5d1'}`,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all'
                  }}>
                    {item.resultado}
                  </Typography>
                )}
                {item.resultado && isSucesso && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '12px' }}>
                    {item.resultado}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
